import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const FEATURES = [
  {
    title: "Design your pass",
    description: "Pick your colours, upload your logo, and lay out exactly what shows on your members' cards — no designer needed.",
  },
  {
    title: "Issue to Google Wallet",
    description: "Members add their pass to their phone in one tap. Update a design or membership status and every issued pass updates instantly — no reprinting.",
  },
  {
    title: "Manage your members",
    description: "Add members one at a time or bulk-import a whole roster from a CSV. Track expiry dates and issue passes in a couple of clicks.",
  },
  {
    title: "Built for clubs",
    description: "Flat annual pricing with no per-member fees, so growing your club doesn't grow your bill unexpectedly.",
  },
];

const CARD_FIELDS = ["Club name", "Member name", "Member photo", "Member ID", "Email", "Status", "Expiry date"];

const STEPS = [
  "Sign in and name your club.",
  "Design your pass — logo, colours, and the fields that matter to your club.",
  "Add your members and issue their passes straight to Google Wallet.",
];

const MOCK_MEMBERS = [
  { name: "Alex Morgan", id: "M-1001", email: "alex@example.com", status: "Active", expiry: "31/03/2027" },
  { name: "Jamie Lee", id: "M-1002", email: "jamie@example.com", status: "Active", expiry: "15/06/2027" },
  { name: "Sam Carter", id: "M-1003", email: "sam@example.com", status: "Expired", expiry: "10/01/2026" },
  { name: "Taylor Reed", id: "M-1004", email: "taylor@example.com", status: "Active", expiry: "01/09/2027" },
];

const STATUS_DOT = {
  Active: "bg-emerald-500",
  Expired: "bg-red-500",
};

// Shared building blocks for a consistent editorial rhythm.
const Container = ({ className = "", children }) => (
  <div className={`max-w-6xl mx-auto px-5 sm:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, children }) => (
  <div className="max-w-2xl mb-12 sm:mb-16">
    <p className="eyebrow mb-4">{eyebrow}</p>
    <h2 className="font-display text-3xl sm:text-[40px] leading-[1.1] font-semibold text-zinc-900">{title}</h2>
    {children && <p className="mt-5 text-lg text-zinc-600 leading-relaxed">{children}</p>}
  </div>
);

const Screenshot = ({ src, alt }) => (
  <div className="panel rounded-2xl p-3 sm:p-6 lg:p-8">
    <img src={src} alt={alt} className="w-full rounded-lg shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]" />
  </div>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <main className="flex-1">
        {/* Hero */}
        <Container className="pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-6">Membership cards for airsoft clubs</p>
              <h1 className="font-display text-[44px] sm:text-6xl lg:text-[68px] leading-[1.02] font-semibold text-zinc-900">
                Digital membership passes for your airsoft club.
              </h1>
              <p className="mt-7 text-lg sm:text-xl text-zinc-600 leading-relaxed max-w-xl">
                Design a custom membership pass, issue it straight to your members' Google Wallet, and manage your
                whole roster from one place. No printing, no laminating, no lost cards.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <Link
                  to="/login"
                  className="inline-flex items-center h-12 px-6 rounded-md bg-[#303030] hover:bg-[#1A1A1A] text-white font-medium transition-colors"
                  data-testid="hero-get-started-btn"
                >
                  Get started free
                </Link>
                <Link
                  to="/pricing"
                  className="group inline-flex items-center gap-2 font-medium text-zinc-900"
                  data-testid="hero-pricing-btn"
                >
                  View pricing
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
              <p className="mt-6 text-sm text-zinc-500">
                Free to try — issue up to 5 test passes, no credit card required.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="panel rounded-3xl py-12 sm:py-16 flex justify-center">
                <img
                  src="/screenshots/pass-preview.png?v=2"
                  alt="Example Club Pass membership pass preview showing member name, ID, status, and expiry"
                  className="w-60 sm:w-64 rounded-[22px] shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45)]"
                />
              </div>
            </div>
          </div>
        </Container>

        {/* Features */}
        <section id="features" className="border-t border-zinc-200">
          <Container className="py-20 sm:py-28">
            <SectionHeader eyebrow="Why Club Pass" title="Everything your club needs to run membership cards." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
              {FEATURES.map((feature, i) => (
                <div key={feature.title} className="border-t border-zinc-900 pt-6">
                  <p className="font-display text-sm font-medium text-zinc-400 mb-6">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-xl font-semibold text-zinc-900 mb-3">{feature.title}</h3>
                  <p className="text-[15px] text-zinc-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Product showcase */}
        <section className="border-t border-zinc-200">
          <Container className="py-20 sm:py-28">
            <SectionHeader eyebrow="The designer" title="See it in action.">
              A real look at the pass designer your club will use — no mockups, this is the actual tool.
            </SectionHeader>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-20 sm:mb-28">
              <div className="lg:col-span-4 order-2 lg:order-1">
                <h3 className="font-display text-2xl font-semibold text-zinc-900 mb-4">Design your pass</h3>
                <p className="text-zinc-600 leading-relaxed">
                  Set your club name, upload your logo and banner, and pick the colours that show when a pass is
                  active or expired. The live preview updates as you go, so you always know exactly what your
                  members will see.
                </p>
              </div>
              <div className="lg:col-span-8 order-1 lg:order-2">
                <Screenshot
                  src="/screenshots/pass-designer.png?v=2"
                  alt="Club Pass designer showing club name, logo, colour, and banner image fields with a live pass preview"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-8">
                <Screenshot
                  src="/screenshots/card-layout.png?v=2"
                  alt="Club Pass card layout builder showing draggable rows and fields with a live pass preview"
                />
              </div>
              <div className="lg:col-span-4">
                <h3 className="font-display text-2xl font-semibold text-zinc-900 mb-4">Arrange your layout</h3>
                <p className="text-zinc-600 leading-relaxed mb-8">
                  Choose which fields appear on each row and reorder them to match how your club actually checks
                  members in.
                </p>
                <p className="eyebrow mb-3">Available fields</p>
                <ul className="grid grid-cols-2 gap-x-6">
                  {CARD_FIELDS.map((field) => (
                    <li key={field} className="text-sm text-zinc-700 py-2.5 border-b border-zinc-200">
                      {field}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Container>
        </section>

        {/* Members mockup */}
        <section className="panel">
          <Container className="py-20 sm:py-28">
            <SectionHeader eyebrow="Members" title="Your whole roster in one place.">
              Add members one at a time or bulk-import a CSV. Track who's active and who's due for renewal at a glance.
            </SectionHeader>
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-6 text-xs uppercase tracking-wider text-zinc-500">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Member ID</TableHead>
                    <TableHead className="hidden sm:table-cell text-xs uppercase tracking-wider text-zinc-500">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                    <TableHead className="hidden sm:table-cell pr-6 text-xs uppercase tracking-wider text-zinc-500">Expiry</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {MOCK_MEMBERS.map((m) => (
                    <TableRow key={m.id} className="hover:bg-zinc-50/60">
                      <TableCell className="pl-6 py-4 font-medium text-zinc-900">{m.name}</TableCell>
                      <TableCell className="py-4 text-zinc-600 tabular-nums">{m.id}</TableCell>
                      <TableCell className="hidden sm:table-cell py-4 text-zinc-600">{m.email}</TableCell>
                      <TableCell className="py-4">
                        <span className="inline-flex items-center gap-2 text-sm text-zinc-700">
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[m.status]}`} />
                          {m.status}
                        </span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell pr-6 py-4 text-zinc-600 tabular-nums">{m.expiry}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-zinc-500 mt-4">Illustrative example — not real member data.</p>
          </Container>
        </section>

        {/* How it works */}
        <section>
          <Container className="py-20 sm:py-28">
            <SectionHeader eyebrow="Getting started" title="Set up in minutes." />
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-12">
              {STEPS.map((step, i) => (
                <li key={step} className="border-t border-zinc-200 pt-6">
                  <p className="font-display text-5xl font-semibold text-zinc-300 mb-6">{i + 1}</p>
                  <p className="text-lg text-zinc-800 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </Container>
        </section>

        {/* CTA band */}
        <section className="bg-[#303030] text-white">
          <Container className="py-20 sm:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl sm:text-5xl leading-[1.08] font-semibold">
                  Ready to modernise your club's membership passes?
                </h2>
                <p className="mt-6 text-zinc-300 text-lg">
                  Flat annual pricing, no per-member fees, and a free trial with no card required.
                </p>
                <p className="mt-6 text-sm text-zinc-400">
                  No printing or laminating <span className="mx-2 text-zinc-600">/</span> Always up to date
                  <span className="mx-2 text-zinc-600">/</span> NZ-based, GST-exclusive pricing
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white hover:bg-zinc-100 text-zinc-900 font-medium transition-colors"
                >
                  Get started free
                  <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
